import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { ApiError, getMyOrders, type OrderSummaryListResponse } from "@/lib/api-client";
import { useCustomerAuth } from "@/lib/auth/customer-auth-context";
import { orderStatusLabel, formatOrderDate } from "./order-format";

/**
 * Historique des commandes du client connecté (`GET /orders`) — port de
 * apps/web/app/(account)/compte/commandes/page.tsx. Chargement via
 * `useQuery` (plutôt que `await` dans un Server Component) ; pagination par
 * curseur conservée à l'identique via le paramètre de requête `?cursor=` —
 * `useSearchParams` (react-router-dom) rejoue le même rôle que
 * `searchParams` côté Next.js.
 *
 * Protection de session déjà assurée par `AccountLayout`/`AccountRouteGuard`
 * (indice local `isLoggedIn`) ; ce composant gère en plus le cas où le
 * cookie httpOnly réel a expiré entre-temps (401 renvoyé par l'API) — cf.
 * commentaire de l'original ("pas de rafraîchissement silencieux ici") :
 * on efface l'indice local et on renvoie vers `/connexion`, exactement comme
 * l'original le faisait déjà pour ce cas précis.
 */
export function CompteCommandesPage() {
  const { logout } = useCustomerAuth();
  const [searchParams] = useSearchParams();
  const cursor = searchParams.get("cursor") ?? undefined;

  const { data, error, isPending } = useQuery({
    queryKey: ["orders", cursor],
    queryFn: () => getMyOrders(undefined, cursor),
  });
  // Repli typé (jamais utilisé pendant `isPending`/`error`, cf. rendu ci-dessous) —
  // évite d'avoir à re-prouver à TypeScript que `data` est défini après les deux
  // branches `isPending`/`error` du ternaire de rendu.
  const orders: OrderSummaryListResponse = data ?? { items: [], nextCursor: null };

  if (error instanceof ApiError && error.status === 401) {
    // Jeton d'accès expiré/invalide : indice local devenu mensonger, on
    // l'efface avant de renvoyer vers /connexion (cf. commentaire de tête).
    logout();
    return <Navigate to="/connexion" replace />;
  }

  return (
    <div className="container-vvs py-8 lg:py-12">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        <aside className="lg:col-span-3">
          <Link to="/compte" className="inline-block text-sm font-medium text-navy hover:text-gold">
            &larr; Retour au tableau de bord
          </Link>
        </aside>

        <div className="lg:col-span-9">
          <h1 className="font-display text-2xl font-bold text-navy">Mes commandes</h1>

          {isPending ? (
            <p className="mt-6 text-sm text-muted">Chargement de vos commandes…</p>
          ) : error ? (
            <div className="mt-6 rounded-sm border border-line bg-white p-8 text-center">
              <p className="text-sm text-muted">Impossible de récupérer vos commandes pour le moment.</p>
            </div>
          ) : orders.items.length === 0 && !cursor ? (
            <div className="mt-6 rounded-sm border border-line bg-white p-8 text-center">
              <p className="text-sm text-muted">Vous n&apos;avez pas encore passé de commande.</p>
              <Link
                to="/catalogue"
                className="mt-4 inline-flex items-center justify-center border border-gold bg-gold px-6 py-3 text-sm font-medium uppercase tracking-wide text-navy hover:border-gold2 hover:bg-gold2"
              >
                Découvrir la boutique
              </Link>
            </div>
          ) : (
            <div className="mt-6 flex flex-col gap-3">
              {orders.items.map((order) => (
                <Link
                  key={order.orderNumber}
                  to={`/compte/commandes/${encodeURIComponent(order.orderNumber)}`}
                  className="flex flex-col gap-3 rounded-sm border border-line bg-white p-4 transition hover:border-navy sm:flex-row sm:items-center sm:gap-4"
                >
                  {order.thumbnailUrl ? (
                    <img
                      src={order.thumbnailUrl}
                      alt=""
                      className="h-16 w-16 flex-none rounded-sm border border-line object-cover"
                    />
                  ) : (
                    <div className="h-16 w-16 flex-none rounded-sm border border-line bg-cream2" aria-hidden="true" />
                  )}

                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-ink">N° {order.orderNumber}</p>
                    <p className="mt-1 text-xs text-muted">
                      Commandée le {formatOrderDate(order.placedAt)} · {order.itemsCount} article
                      {order.itemsCount > 1 ? "s" : ""}
                    </p>
                  </div>

                  <div className="flex flex-none items-center gap-4 sm:flex-col sm:items-end sm:gap-1">
                    <span className="inline-block rounded-sm border border-line bg-cream2 px-2 py-1 text-xs font-medium uppercase tracking-wide text-navy">
                      {orderStatusLabel(order.status)}
                    </span>
                    <span className="text-sm font-semibold text-ink">{order.total.formatted}</span>
                  </div>
                </Link>
              ))}

              {orders.nextCursor ? (
                <div className="mt-4 flex justify-center">
                  <Link
                    to={`/compte/commandes?cursor=${encodeURIComponent(orders.nextCursor)}`}
                    className="rounded-sm border border-line bg-white px-6 py-2.5 text-sm font-medium text-ink hover:border-navy"
                  >
                    Voir plus
                  </Link>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
